import { Flex, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import Image from 'next/image';
import type React from 'react';
import styled from 'styled-components';

export const STORY_WIDTH = 400;
export const STORY_WIDTH_MOBILE = 340;
export const STORY_HEIGHT = 324;
export const STORY_HEIGHT_MOBILE = 320;
export const STORY_MARGIN = 24;
export const STORY_MARGIN_MOBILE = 16;

const StoryCardWrapper = styled(Flex)`
  position: relative;
  background: ${themedColor('white')};
  height: ${STORY_HEIGHT_MOBILE}px;
  width: ${STORY_WIDTH_MOBILE}px;
  box-shadow:
    0px 1.28761px 7.72566px rgba(0, 0, 0, 0.06),
    0px 30.2589px 29.615px rgba(0, 0, 0, 0.16);
  border-radius: 10px;
  padding: 18px 24px;
  margin: 0 ${STORY_MARGIN_MOBILE}px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.06), 0px 47px 46px rgba(0, 0, 0, 0.16);
        margin: 0 ${STORY_MARGIN}px;
        height: ${STORY_HEIGHT}px;
        width: ${STORY_WIDTH}px;
        padding: 24px 36px;
    `}
`;

const StoryImage = styled(Image)`
  border-radius: 50%;
`;

const Heading = styled(Flex).attrs({
  flexDirection: 'row',
  alignItems: 'center',
})`
  gap: 16px;
  margin-bottom: 16px;
`;

const Name = styled.span`
  ${font('main', { fontSize: '2.2rem', lineHeight: '1.5', fontWeight: 700 })};
  text-transform: capitalize;
`;

const Handle = styled.span`
  ${font('main', { fontSize: '1.4rem', lineHeight: '1.5' })};
`;

const Costs = styled(Flex).attrs({ flexDirection: 'column' })`
  ${font('main', { fontSize: '1.6rem', lineHeight: '1.5' })};
  position: absolute;
  bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        bottom: 24px;
    `}
`;

const Brand = styled.span`
  color: ${themedColor('primary')};
`;

const Bold = styled.span`
  font-weight: 700;
`;

const TweetText = styled.p`
  text-wrap: balance;
  ${font('main', { fontSize: '1.6rem', lineHeight: '2.4rem' })};
`;

export interface Story {
  name: string;
  img: string;
  description: string;
  injury: string;
  text: string;
  vetBill: string;
  savedWithLemonade: string;
}

export const StoryCard: React.FC<Story> = ({
  img,
  name,
  injury,
  description,
  text,
  vetBill,
  savedWithLemonade,
  ...restProps
}) => {
  return (
    <StoryCardWrapper flexDirection="column" {...restProps}>
      <Heading>
        <StoryImage src={img} alt={name} height={50} width={50} />
        <Flex flexDirection="column" flex="1">
          <Name>{name}</Name>
          <Handle>{description}</Handle>
        </Flex>
      </Heading>
      <TweetText>
        What happened?&nbsp;
        <Bold>{injury}</Bold>
      </TweetText>
      <TweetText>{parse(text)}</TweetText>
      <Costs>
        <span>
          Vet Bill:&nbsp;
          <Bold>{vetBill}</Bold>
        </span>
        <span>
          <Brand>Saved With Lemonade:</Brand>
          &nbsp;
          {savedWithLemonade}
        </span>
      </Costs>
    </StoryCardWrapper>
  );
};
